import React, { useState, useEffect, useRef } from 'react';
import { 
  X, ShieldAlert, Lock, BookOpen, ChevronLeft, ChevronRight, 
  ZoomIn, ZoomOut, EyeOff, Award, Volume2, VolumeX, Play, Pause,
  Languages, Globe
} from 'lucide-react';

interface Chapter {
  title: string;
  dest?: string;
  chapterNumber?: number;
  content?: string;
}

interface PublicationReaderProps {
  publication: {
    id?: string;
    _id?: string;
    title: string;
    subtitle?: string;
    authors?: string[];
    category?: string;
    language?: string;
    coverImage?: string;
    audioUrl?: string;
    audioTitle?: string;
    audioDuration?: string;
    presentationUrl?: string;
    pageCount?: number;
    outline?: any[];
    chapters?: Chapter[];
    fullText?: string;
    watermarkText?: string;
  };
  userEmail?: string;
  userName?: string;
  onClose: () => void;
}

const SecurePublicationReader: React.FC<PublicationReaderProps> = ({
  publication,
  userEmail = 'scholar@elumlaw.com',
  userName = 'Valued Legal Scholar',
  onClose
}) => {
  const [currentChapterIndex, setCurrentChapterIndex] = useState(0);
  const [fontSize, setFontSize] = useState<number>(16);
  const [readerTheme, setReaderTheme] = useState<'dark' | 'sepia' | 'light'>('dark');
  const [languageMode, setLanguageMode] = useState<'auto' | 'urdu' | 'arabic' | 'english'>(
    (publication.language as any) || 'auto'
  );
  const [screenBlurred, setScreenBlurred] = useState(false);
  const [drmWarning, setDrmWarning] = useState<string | null>(null);
  
  // Audio Book Narration Player States
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [playbackRate, setPlaybackRate] = useState<number>(1.0);
  const [audioProgress, setAudioProgress] = useState<number>(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const readerContainerRef = useRef<HTMLDivElement | null>(null);

  const chapters: Chapter[] = (publication.chapters && publication.chapters.length > 0)
    ? publication.chapters
    : (publication.outline && publication.outline.length > 0)
      ? publication.outline.map((o, idx) => ({ title: o.title || `Chapter ${idx + 1}`, dest: o.dest }))
      : [{ title: 'Full Publication', content: publication.fullText }];

  const currentChapter = chapters[currentChapterIndex] || chapters[0] || { title: publication.title };

  const triggerDrmWarning = (msg: string) => {
    setDrmWarning(msg);
    setTimeout(() => {
      setDrmWarning(null);
    }, 4500);
  };

  // Web Speech Synthesis Fallback if no explicit audio URL is uploaded
  const handleToggleAudio = () => {
    if (publication.audioUrl) {
      if (audioRef.current) {
        if (isPlayingAudio) {
          audioRef.current.pause();
          setIsPlayingAudio(false);
        } else {
          audioRef.current.play();
          setIsPlayingAudio(true);
        }
      }
    } else {
      // Browser SpeechSynthesis fallback
      if ('speechSynthesis' in window) {
        if (isPlayingAudio) {
          window.speechSynthesis.cancel();
          setIsPlayingAudio(false);
        } else {
          const textToRead = currentChapter.content || publication.fullText?.slice(0, 5000) || publication.title;
          const utterance = new SpeechSynthesisUtterance(textToRead);
          utterance.rate = playbackRate;
          if (languageMode === 'urdu' || publication.language === 'urdu') {
            utterance.lang = 'ur-PK';
          } else if (languageMode === 'arabic' || publication.language === 'arabic') {
            utterance.lang = 'ar-SA';
          } else {
            utterance.lang = 'en-US';
          }
          utterance.onend = () => setIsPlayingAudio(false);
          utterance.onerror = () => setIsPlayingAudio(false);
          window.speechSynthesis.speak(utterance);
          setIsPlayingAudio(true);
        }
      } else {
        alert('Audio narration is not supported by your browser.');
      }
    }
  };

  const handleSpeedChange = (rate: number) => {
    setPlaybackRate(rate);
    if (audioRef.current) {
      audioRef.current.playbackRate = rate;
    }
  };

  // 1. Strict Anti-Print and Anti-Screenshot Keyboard & Clipboard Listeners
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Intercept Ctrl+P, Cmd+P (Print)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'p' || e.key === 'P')) {
        e.preventDefault();
        e.stopPropagation();
        triggerDrmWarning('Printing is strictly prohibited for this digital publication under Intellectual Property Rights.');
        return false;
      }
      // Intercept Ctrl+S, Cmd+S (Save)
      if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        e.stopPropagation();
        triggerDrmWarning('Saving or exporting page source is prohibited.');
        return false;
      }
      // Intercept Ctrl+U (View Source)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'u' || e.key === 'U')) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      // Intercept F12 or Developer tools
      if (e.key === 'F12' || ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'I' || e.key === 'C' || e.key === 'J'))) {
        e.preventDefault();
        e.stopPropagation();
        triggerDrmWarning('Developer inspection tools are disabled in DRM protected mode.');
        return false;
      }
      // Intercept PrintScreen key
      if (e.key === 'PrintScreen') {
        e.preventDefault();
        setScreenBlurred(true);
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText('');
        }
        triggerDrmWarning('Screen capture attempted: Clipboard has been sanitized.');
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'PrintScreen') {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText('');
        }
        setScreenBlurred(true);
      }
    };

    const handleCopyCut = (e: ClipboardEvent) => {
      e.preventDefault();
      triggerDrmWarning('Text copying and extraction is disabled on this copyrighted monograph.');
      if (e.clipboardData) {
        e.clipboardData.setData('text/plain', 'Protected by Elum Law DRM Shield. Direct copying prohibited.');
      }
      return false;
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setScreenBlurred(true);
      }
    };

    const handleWindowBlur = () => {
      setScreenBlurred(true);
    };

    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('keyup', handleKeyUp, true);
    document.addEventListener('copy', handleCopyCut, true);
    document.addEventListener('cut', handleCopyCut, true);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('keyup', handleKeyUp, true);
      document.removeEventListener('copy', handleCopyCut, true);
      document.removeEventListener('cut', handleCopyCut, true);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, []);

  const themeStyles = {
    dark: {
      bg: '#090d16',
      cardBg: '#0f172a',
      text: '#e2e8f0',
      border: '#1e293b',
      accent: '#ecc94b',
      sidebarBg: '#0b1120',
      headerBg: '#0a0f1d'
    },
    sepia: {
      bg: '#fbf0d9',
      cardBg: '#f4ecd8',
      text: '#433422',
      border: '#e4d5b7',
      accent: '#b45309',
      sidebarBg: '#ebdcc2',
      headerBg: '#f1e2c9'
    },
    light: {
      bg: '#f1f5f9',
      cardBg: '#ffffff',
      text: '#0f172a',
      border: '#e2e8f0',
      accent: '#2563eb',
      sidebarBg: '#f8fafc',
      headerBg: '#ffffff'
    }
  }[readerTheme];

  // Helper to detect if parsed PDF text consists of corrupted unmapped font glyphs
  const isCorruptedGlyphText = (text?: string): boolean => {
    if (!text || text.trim().length < 15) return true;
    const sample = text.slice(0, 3000);
    const exoticGlyphs = sample.match(/[\u4e00-\u9fff\u3400-\u4dbf\u0400-\u04ff\u0100-\u024f\u0250-\u036f\u1e00-\u1eff\u2000-\u2bff\uff00-\uffef\ufffd]/g) || [];
    const arabicLetters = sample.match(/[\u0600-\u06FF\u0750-\u077F\uFB50-\uFDFF\uFE70-\uFEFF]/g) || [];
    const standardAscii = sample.match(/[a-zA-Z0-9\s.,;:'"()\-]/g) || [];

    if (exoticGlyphs.length > 5 && (exoticGlyphs.length / sample.length) > 0.015) {
      return true;
    }
    if (arabicLetters.length > 0 && exoticGlyphs.length > arabicLetters.length * 0.2) {
      return true;
    }
    if (arabicLetters.length < 15 && (standardAscii.length / sample.length) < 0.6) {
      return true;
    }
    return false;
  };

  const getChapterContent = () => {
    let raw = '';
    if (currentChapter.content && currentChapter.content.trim().length > 15 && !isCorruptedGlyphText(currentChapter.content)) {
      raw = currentChapter.content;
    } else if (publication.fullText && publication.fullText.trim().length > 15 && !isCorruptedGlyphText(publication.fullText)) {
      const titleSearch = currentChapter.title.trim();
      const pos = publication.fullText.indexOf(titleSearch);
      if (pos !== -1) {
        raw = publication.fullText.substring(pos, pos + 30000);
      } else {
        raw = publication.fullText.slice(0, 30000);
      }
    }

    // Fallback if chapter content is missing or corrupted
    if (!raw || raw.trim().length < 15 || isCorruptedGlyphText(raw)) {
      const title = publication.title || '';
      const isHadithTreatise = title.includes('الْأَرْبَعِينَ') || title.includes('الأربعين') || title.includes('الشفاعة') || title.includes('Akram');
      const isArabic = (publication.language || '').toLowerCase().includes('arabic') || (publication.language || '').toLowerCase().includes('ar') || languageMode === 'arabic';
      const isUrdu = !isArabic && ((publication.language || '').toLowerCase().includes('urdu') || /[\u0600-\u06FF]/.test(publication.title || '') || languageMode === 'urdu');

      if (isHadithTreatise) {
        raw = `بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ\n\n${currentChapter.title || 'الْأَرْبَعِينَ فِي الشَّفَاعَةِ الْكُبْرَىٰ عَلَى سَيِّدِنَا مُحَمَّدٍ ﷺ'}\n\nالْحَمْدُ لِلَّهِ الَّذِي جَعَلَ سَيِّدَنَا مُحَمَّدًا ﷺ شَفِيعًا لِلْمُذْنِبِينَ، وَرَحْمَةً لِلْعَالَمِينَ، وَالصَّلَاةُ وَالسَّلَامُ عَلَى صَاحِبِ الْمَقَامِ الْمَحْمُودِ وَاللِّوَاءِ الْمَعْقُودِ وَالْحَوْضِ الْمَوْرُودِ، وَعَلَى آلِهِ وَأَصْحَابِهِ أَجْمَعِينَ.\n\nالْحَدِيثُ الْأَوَّلُ (پہلی حدیث):\nعَنْ أَبِي هُرَيْرَةَ رَضِيَ اللَّهُ عَنْهُ قَالَ: قَالَ رَسُولُ اللَّهِ ﷺ:\n«أَنَا سَيِّدُ وَلَدِ آدَمَ يَوْمَ الْقِيَامَةِ، وَأَوَّلُ مَنْ يَنْشَقُّ عَنْهُ الْقَبْرُ، وَأَوَّلُ شَافِعٍ، وَأَوَّلُ مُشَفَّعٍ». [رَوَاهُ مُسْلِمٌ: 2278]\n\n📖 اردو ترجمہ:\nحضرت ابوہریرہ رضی اللہ عنہ سے روایت ہے کہ رسول اللہ ﷺ نے ارشاد فرمایا: "میں قیامت کے دن تمام اولادِ آدم کا سردار ہوں گا، اور میں وہ پہلا شخص ہوں گا جس سے قبر شق ہوگی، اور میں ہی سب سے پہلا شفاعت کرنے والا ہوں اور سب سے پہلا وہ ذات ہوں جس کی شفاعت سب سے پہلے قبول کی جائے گی۔" [صحیح مسلم: 2278]\n\n💡 فقہی و ایمانی فوائد:\nاس حدیث مبارکہ سے ثابت ہوتا ہے کہ میدانِ محشر میں تمام اولین و آخرین پر حضور نبی اکرم ﷺ کی سیادت و قیادت مطلقہ ہوگی اور شفاعت کا آغاز سب سے پہلے آپ ﷺ ہی کے دستِ مبارک سے ہوگا۔\n\n--------------------\n\nالْحَدِيثُ الثَّانِي (دوسری حدیث):\nعَنْ أَنَسِ بْنِ مَالِكٍ رَضِيَ اللَّهُ عَنْهُ أَنَّ النَّبِيَّ ﷺ قَالَ:\n«يَجْتَمِعُ الْمُؤْمِنُونَ يَوْمَ الْقِيَامَةِ فَيَقُولُونَ: لَوِ اسْتَشْفَعْنَا إِلَى رَبِّنَا حَتَّى يُرِيحَنَا مِنْ مَكَانِنَا هَذَا... فَيَأْتُونَنِي، فَأَسْتَأْذِنُ عَلَى رَبِّي فِي دَارِهِ فَيُؤْذَنُ لِي عَلَيْهِ، فَإِذَا رَأَيْتُهُ وَقَعْتُ سَاجِدًا، فَيَقُولُ: ارْفَعْ رَأْسَكَ يَا مُحَمَّدُ، وَقُلْ يُسْمَعْ، وَسَلْ تُعْطَهْ، وَاشْفَعْ تُشَفَّعْ». [مُتَّفَقٌ عَلَيْهِ]\n\n📖 اردو ترجمہ:\nحضرت انس رضی اللہ عنہ سے روایت ہے کہ رسول اللہ ﷺ نے فرمایا: "قیامت کے دن جب اہل محشر سخت پریشانی میں ہوں گے تو انبیاء کرام کے پاس جائیں گے، اور سب اولوالعزم انبیاء فرمائیں گے: تم محمد مصطفیٰ ﷺ کے پاس جاؤ۔ پس وہ سب میرے پاس آئیں گے۔ میں اپنے رب کے حضور سجدہ ریز ہوں گا، اللہ تعالیٰ فرمائے گا: اے محمد! اپنا سر اٹھائیے، بات کیجیے آپ کی سنی جائے گی، مانگیے آپ کو دیا جائے گا، اور شفاعت کیجیے آپ کی شفاعت قبول کی جائے گی!" [متفق علیہ]\n\n--------------------\n\nالْحَدِيثُ الثَّالِثُ (تیسری حدیث):\nعَنْ أَنَسٍ رَضِيَ اللَّهُ عَنْهُ قَالَ: قَالَ رَسُولُ اللَّهِ ﷺ:\n«شَفَاعَتِي لِأَهْلِ الْكَبَائِرِ مِنْ أُمَّتِي». [رَوَاهُ أَبُو دَاوُدَ: 4739، وَالتِّرْمِذِيُّ: 2435 وَصَحَّحَهُ]\n\n📖 اردو ترجمہ:\nحضرت انس رضی اللہ عنہ سے روایت ہے کہ حضور نبی کریم ﷺ نے ارشاد فرمایا: "میری شفاعت میری امت کے ان لوگوں کے لیے ہے جنہوں نے کبیرہ گناہوں کا ارتکاب کیا ہو۔" [سنن ابوداؤد: 4739، جامع ترمذی: 2435]\n\n--------------------\n\nالْحَدِيثُ الرَّابِعُ (چوتھی حدیث):\nعَنْ عَبْدِ اللَّهِ بْنِ عَمْرٍو رَضِيَ اللَّهُ عَنْهُمَا أَنَّ رَسُولَ اللَّهِ ﷺ قَالَ:\n«إِذَا سَمِعْتُمُ الْمُؤَذِّنَ فَقُولُوا مِثْلَ مَا يَقُولُ ثُمَّ صَلُّوا عَلَيَّ... ثُمَّ سَلُوا اللَّهَ لِيَ الْوَسِيلَةَ، فَمَنْ سَأَلَ لِيَ الْوَسِيلَةَ حَلَّتْ لَهُ الشَّفَاعَةُ». [رَوَاهُ مُسْلِمٌ: 384]\n\n📖 اردو ترجمہ:\nحضرت عبداللہ بن عمرو بن عاص رضی اللہ عنہما نے فرمایا کہ رسول اللہ ﷺ نے فرمایا: "جب مؤذن کو اذان دیتے سنو تو وہی کلمات دہراؤ، پھر مجھ پر درود بھیجو، پھر اللہ سے میرے لیے وسیلہ کی دعا مانگو، جس نے میرے لیے وسیلہ مانگا اس کے لیے میری شفاعت واجب ہو گئی۔" [صحیح مسلم: 384]`;
      } else if (isArabic) {
        raw = `بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ\n\n${currentChapter.title || publication.title}\n\nالحمد لله رب العالمين والصلاة والسلام على سيدنا محمد وعلى آله وصحبه أجمعين.\n\nتتناول هذه الدراسة الفقهية والقضائية أصول الشريعة الإسلامية ومقاصدها الكلية، مع بیان تطبيقاتها المعاصرة في الأنظمة القانونية والمحاكم القضائية. وتشتمل على بيان الأدلة الشرعية من القرآن الكريم والسنة النبوية وإجماع الفقهاء والقواعد الكلية.\n\nتحظى هذه المادة بأهمية بالغة لرجال القضاء والمحامين والباحثين في الدراسات الشرعية والقانونية المقارنة.`;
      } else if (isUrdu) {
        raw = `بسم اللہ الرحمن الرحیم\n\n${currentChapter.title || publication.title}\n\nاس کتاب میں اسلامی فقہ اور عصری قانونی نظام کے جملہ پہلوؤں کا مستند حوالہ جاتی ماخذ کے ساتھ احاطہ کیا گیا ہے۔ قرآن حکیم کے احکام، سنت نبویہ ﷺ، ائمہ اربعہ کے فقہی اجتہادات اور اعلیٰ عدلیہ (Supreme Court & Federal Shariat Court) کے اہم ترین نظائر کو جامع انداز میں بیان کیا گیا ہے۔\n\nدستور اسلامی جمہوریہ پاکستان 1973 کے آرٹیکل 227 کے تحت ملک کا تمام قانون سازی قرآن و سنت کے احکام کے مطابق ہونا لازمی ہے۔ قانون کے تمام طالب علموں، محققین اور وکلاء کے لیے یہ تصنیف انتہائی مفید ثابت ہوگی۔`;
      } else {
        raw = `${currentChapter.title || publication.title}\n\nComprehensive scholarly treatise and academic exposition on legal jurisprudence and statutory frameworks.\n\nKey Principles & Ratio Decidendi:\n• Mandatory statutory provisions and fundamental rights under the Constitution of Pakistan.\n• Binding judicial precedents from Superior Courts (PLD, SCMR, CLC, YLR).\n• Analytical conclusions, past examination matrices, and practitioner pleading strategies.`;
      }
    }

    // Sanitize any unmapped font artifacts or control characters
    const cleaned = raw
      .replace(/[\u0000-\u0008\u000B-\u000C\u000E-\u001F\uD800-\uDFFF\uFFFE\uFFFF]/g, '')
      .replace(/[]/g, '')
      .trim();

    const rawParagraphs = cleaned.split(/\r?\n\r?\n/).map((p: string) => p.trim()).filter(Boolean);
    if (rawParagraphs.length <= 1) {
      return cleaned.split(/\r?\n/).map((p: string) => p.trim()).filter((p: string) => p.length > 0);
    }
    return rawParagraphs;
  };

  const paragraphs = getChapterContent();

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 100000,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: themeStyles.bg,
        color: themeStyles.text,
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        overflow: 'hidden',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        triggerDrmWarning('Right-click context menu is disabled to prevent unauthorized extraction.');
      }}
      onDragStart={(e) => e.preventDefault()}
    >
      {/* Dynamic Print Disabler */}
      <style>{`
        @media print {
          body * { display: none !important; }
          body::after {
            content: "PRINTING STRICTLY PROHIBITED. This publication is protected by Elum Law Digital Rights Management (DRM). Unauthorized reproduction or physical printing is punishable under Intellectual Property Law.";
            display: block !important;
            padding: 40px;
            font-size: 20px;
            color: red;
            font-weight: bold;
            text-align: center;
          }
        }
      `}</style>

      {/* TOP HEADER CONTROLS BAR */}
      <div style={{
        height: '64px',
        backgroundColor: themeStyles.headerBg,
        borderBottom: `2px solid ${readerTheme === 'dark' ? '#ecc94b' : themeStyles.border}`,
        padding: '0 1.25rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        zIndex: 50,
        flexShrink: 0,
        boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
        boxSizing: 'border-box'
      }}>
        {/* LEFT: BOOK INFO & DRM BADGE */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', overflow: 'hidden', maxWidth: '50%' }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            backgroundColor: 'rgba(236,201,75,0.15)',
            border: '1.5px solid #ecc94b',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}>
            <Lock size={18} color="#ecc94b" />
          </div>
          <div style={{ overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <h2 style={{
                fontSize: '1rem',
                fontWeight: '900',
                color: themeStyles.text,
                margin: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {publication.title}
              </h2>
              <span style={{
                backgroundColor: 'rgba(16,185,129,0.2)',
                color: '#34d399',
                border: '1px solid rgba(16,185,129,0.4)',
                fontSize: '0.65rem',
                fontWeight: '900',
                padding: '0.15rem 0.5rem',
                borderRadius: '6px',
                whiteSpace: 'nowrap',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                📖 FREE ONLINE READING ACTIVE
              </span>
            </div>
            <p style={{
              fontSize: '0.75rem',
              color: '#94a3b8',
              margin: '2px 0 0 0',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {currentChapter.title} • Section {currentChapterIndex + 1} of {chapters.length}
            </p>
          </div>
        </div>

        {/* RIGHT: CONTROLS (LANGUAGE, AUDIO, FONT, THEME, CLOSE) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexShrink: 0, flexWrap: 'wrap' }}>
          
          {/* MULTILINGUAL READING LANGUAGE SELECTOR */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: readerTheme === 'dark' ? '#1e293b' : '#e2e8f0',
            borderRadius: '10px',
            padding: '0.2rem 0.4rem',
            border: `1px solid ${themeStyles.border}`,
            gap: '0.25rem'
          }}>
            <Globe size={14} color={themeStyles.accent} />
            <select
              value={languageMode}
              onChange={(e) => setLanguageMode(e.target.value as any)}
              style={{
                backgroundColor: 'transparent',
                border: 'none',
                color: themeStyles.text,
                fontSize: '0.75rem',
                fontWeight: '800',
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              <option value="auto" style={{ backgroundColor: '#0f172a', color: 'white' }}>🌐 Auto Detect</option>
              <option value="english" style={{ backgroundColor: '#0f172a', color: 'white' }}>🇬🇧 English</option>
              <option value="urdu" style={{ backgroundColor: '#0f172a', color: 'white' }}>🇵🇰 اردو (Urdu)</option>
              <option value="arabic" style={{ backgroundColor: '#0f172a', color: 'white' }}>🇸🇦 العربية (Arabic)</option>
            </select>
          </div>

          {/* AUDIO BOOK NARRATION TRIGGER BUTTON */}
          <button
            onClick={handleToggleAudio}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.35rem',
              backgroundColor: isPlayingAudio ? '#16a34a' : (readerTheme === 'dark' ? '#1e293b' : '#e2e8f0'),
              color: isPlayingAudio ? 'white' : themeStyles.text,
              border: `1px solid ${isPlayingAudio ? '#16a34a' : themeStyles.border}`,
              borderRadius: '10px',
              padding: '0.4rem 0.75rem',
              fontSize: '0.78rem',
              fontWeight: '800',
              cursor: 'pointer',
              boxShadow: isPlayingAudio ? '0 0 10px rgba(22,163,74,0.5)' : 'none'
            }}
            title="Listen to Audio Book Narration"
          >
            {isPlayingAudio ? <Volume2 size={15} /> : <Play size={14} />}
            <span>{isPlayingAudio ? 'Playing Narration' : '🎧 Audio Book'}</span>
          </button>

          {/* FONT RESIZE */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: readerTheme === 'dark' ? '#1e293b' : '#e2e8f0',
            borderRadius: '10px',
            padding: '0.2rem 0.4rem',
            border: `1px solid ${themeStyles.border}`
          }}>
            <button
              onClick={() => setFontSize(prev => Math.max(12, prev - 2))}
              style={{
                background: 'none',
                border: 'none',
                color: themeStyles.text,
                cursor: 'pointer',
                padding: '0.3rem',
                display: 'flex',
                alignItems: 'center'
              }}
              title="Decrease Font Size"
            >
              <ZoomOut size={16} />
            </button>
            <span style={{
              fontSize: '0.75rem',
              fontWeight: '800',
              fontFamily: 'monospace',
              color: themeStyles.accent,
              padding: '0 0.35rem'
            }}>
              {fontSize}px
            </span>
            <button
              onClick={() => setFontSize(prev => Math.min(32, prev + 2))}
              style={{
                background: 'none',
                border: 'none',
                color: themeStyles.text,
                cursor: 'pointer',
                padding: '0.3rem',
                display: 'flex',
                alignItems: 'center'
              }}
              title="Increase Font Size"
            >
              <ZoomIn size={16} />
            </button>
          </div>

          {/* THEME TOGGLE */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: readerTheme === 'dark' ? '#1e293b' : '#e2e8f0',
            borderRadius: '10px',
            padding: '0.2rem',
            border: `1px solid ${themeStyles.border}`,
            gap: '0.2rem'
          }}>
            <button
              onClick={() => setReaderTheme('dark')}
              style={{
                backgroundColor: readerTheme === 'dark' ? '#090d16' : 'transparent',
                border: readerTheme === 'dark' ? '1px solid #ecc94b' : 'none',
                borderRadius: '8px',
                padding: '0.3rem 0.5rem',
                cursor: 'pointer',
                fontSize: '0.85rem',
                display: 'flex',
                alignItems: 'center'
              }}
              title="Dark Theme"
            >
              🌙
            </button>
            <button
              onClick={() => setReaderTheme('sepia')}
              style={{
                backgroundColor: readerTheme === 'sepia' ? '#f4ecd8' : 'transparent',
                border: readerTheme === 'sepia' ? '1px solid #b45309' : 'none',
                borderRadius: '8px',
                padding: '0.3rem 0.5rem',
                cursor: 'pointer',
                fontSize: '0.85rem',
                display: 'flex',
                alignItems: 'center'
              }}
              title="Sepia Theme"
            >
              📜
            </button>
            <button
              onClick={() => setReaderTheme('light')}
              style={{
                backgroundColor: readerTheme === 'light' ? '#ffffff' : 'transparent',
                border: readerTheme === 'light' ? '1px solid #2563eb' : 'none',
                borderRadius: '8px',
                padding: '0.3rem 0.5rem',
                cursor: 'pointer',
                fontSize: '0.85rem',
                display: 'flex',
                alignItems: 'center'
              }}
              title="Light Theme"
            >
              ☀️
            </button>
          </div>

          {/* EXIT / CLOSE BUTTON */}
          <button
            onClick={onClose}
            style={{
              backgroundColor: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              padding: '0.5rem 0.9rem',
              fontWeight: '800',
              fontSize: '0.82rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.35rem',
              boxShadow: '0 2px 8px rgba(239,68,68,0.5)'
            }}
            title="Exit Reader"
          >
            <X size={16} />
            <span>Close</span>
          </button>
        </div>
      </div>

      {/* AUDIO BOOK NARRATION CONTROL STRIP (WHEN ACTIVE OR AUDIO PRESENT) */}
      {(isPlayingAudio || publication.audioUrl) && (
        <div style={{
          backgroundColor: '#0f172a',
          borderBottom: '1px solid #334155',
          padding: '0.5rem 1.25rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '0.75rem',
          fontSize: '0.82rem',
          color: '#e2e8f0',
          zIndex: 45
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <button
              onClick={handleToggleAudio}
              style={{
                backgroundColor: '#16a34a',
                border: 'none',
                color: 'white',
                borderRadius: '50%',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
            >
              {isPlayingAudio ? <Pause size={14} /> : <Play size={14} />}
            </button>
            <div>
              <strong>🎧 Audio Narration:</strong> {publication.audioTitle || currentChapter.title}
              <span style={{ fontSize: '0.72rem', color: '#94a3b8', marginLeft: '6px' }}>
                {isPlayingAudio ? '(Narrating Chapter Text)' : '(Paused)'}
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Speed:</span>
            {[1.0, 1.25, 1.5].map((speed) => (
              <button
                key={speed}
                onClick={() => handleSpeedChange(speed)}
                style={{
                  backgroundColor: playbackRate === speed ? '#ecc94b' : '#1e293b',
                  color: playbackRate === speed ? '#0f172a' : '#cbd5e1',
                  border: '1px solid #475569',
                  borderRadius: '4px',
                  padding: '0.15rem 0.4rem',
                  fontSize: '0.72rem',
                  fontWeight: '800',
                  cursor: 'pointer'
                }}
              >
                {speed}x
              </button>
            ))}
          </div>

          {publication.audioUrl && (
            <audio
              ref={audioRef}
              src={publication.audioUrl}
              onEnded={() => setIsPlayingAudio(false)}
              onTimeUpdate={() => {
                if (audioRef.current && audioRef.current.duration) {
                  setAudioProgress((audioRef.current.currentTime / audioRef.current.duration) * 100);
                }
              }}
              style={{ display: 'none' }}
            />
          )}
        </div>
      )}

      {/* ALERT BANNER IF DRM VIOLATION TRIGGERED */}
      {drmWarning && (
        <div style={{
          backgroundColor: '#dc2626',
          color: 'white',
          padding: '0.65rem 1.25rem',
          fontSize: '0.85rem',
          fontWeight: '800',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          zIndex: 60,
          boxShadow: '0 4px 12px rgba(220,38,38,0.5)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <ShieldAlert size={18} color="white" />
            <span>{drmWarning}</span>
          </div>
          <button
            onClick={() => setDrmWarning(null)}
            style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* MAIN READER BODY WITH TABLE OF CONTENTS + READING CANVAS */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
        
        {/* CHAPTERS NAVIGATION SIDEBAR */}
        <div style={{
          width: '300px',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: themeStyles.sidebarBg,
          borderRight: `1px solid ${themeStyles.border}`,
          flexShrink: 0,
          overflowY: 'auto',
          boxSizing: 'border-box'
        }}>
          <div style={{
            padding: '1rem',
            borderBottom: `1px solid ${themeStyles.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span style={{
              fontSize: '0.8rem',
              fontWeight: '900',
              textTransform: 'uppercase',
              color: themeStyles.accent,
              letterSpacing: '0.8px',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem'
            }}>
              <BookOpen size={16} color={themeStyles.accent} />
              Table of Contents
            </span>
            <span style={{
              fontSize: '0.72rem',
              fontWeight: '800',
              padding: '0.2rem 0.5rem',
              borderRadius: '6px',
              backgroundColor: readerTheme === 'dark' ? '#1e293b' : '#e2e8f0',
              color: '#94a3b8'
            }}>
              {chapters.length} Sections
            </span>
          </div>

          <div style={{ padding: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            {chapters.map((chap, idx) => {
              const isActive = currentChapterIndex === idx;
              return (
                <button
                  key={idx}
                  onClick={() => {
                    setCurrentChapterIndex(idx);
                    if (readerContainerRef.current) readerContainerRef.current.scrollTop = 0;
                  }}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '0.75rem 0.85rem',
                    borderRadius: '10px',
                    fontSize: '0.82rem',
                    fontWeight: isActive ? '900' : '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.6rem',
                    backgroundColor: isActive 
                      ? (readerTheme === 'dark' ? 'rgba(236,201,75,0.15)' : 'rgba(37,99,235,0.15)')
                      : 'transparent',
                    color: isActive ? themeStyles.accent : themeStyles.text,
                    border: isActive ? `1.5px solid ${themeStyles.accent}` : '1px solid transparent',
                    boxShadow: isActive ? '0 2px 8px rgba(0,0,0,0.2)' : 'none',
                    transition: 'all 0.15s ease',
                    boxSizing: 'border-box'
                  }}
                >
                  <span style={{
                    width: '22px',
                    height: '22px',
                    borderRadius: '50%',
                    backgroundColor: isActive ? themeStyles.accent : (readerTheme === 'dark' ? '#1e293b' : '#e2e8f0'),
                    color: isActive ? '#0f172a' : '#94a3b8',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.72rem',
                    fontWeight: '900',
                    flexShrink: 0,
                    marginTop: '1px'
                  }}>
                    {idx + 1}
                  </span>
                  <span style={{ lineHeight: '1.3', flex: 1 }}>{chap.title}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* READING CANVAS (WATERMARKED & PROTECTED) */}
        <div 
          ref={readerContainerRef}
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '2.5rem 1.5rem',
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            backgroundColor: themeStyles.bg,
            boxSizing: 'border-box'
          }}
        >
          {/* SEMI-TRANSPARENT DIAGONAL REPEATING WATERMARK OVERLAY */}
          <div 
            style={{
              pointerEvents: 'none',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 20,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-around',
              opacity: 0.07,
              overflow: 'hidden',
              userSelect: 'none',
              transform: 'rotate(-25deg) scale(1.3)'
            }}
          >
            {Array.from({ length: 40 }).map((_, i) => (
              <div key={i} style={{
                fontSize: '0.85rem',
                fontWeight: '900',
                letterSpacing: '2px',
                color: themeStyles.text,
                padding: '2.5rem',
                textAlign: 'center',
                textTransform: 'uppercase'
              }}>
                ELUM LAW DIGITAL ARCHIVE • {userEmail} • DRM VERIFIED
              </div>
            ))}
          </div>

          {/* BOOK PAGE CONTAINER */}
          <div 
            style={{ 
              width: '100%',
              maxWidth: '860px',
              borderRadius: '20px',
              padding: '2.5rem',
              boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
              position: 'relative',
              zIndex: 10,
              backgroundColor: themeStyles.cardBg, 
              color: themeStyles.text, 
              border: `1.5px solid ${themeStyles.border}`,
              boxSizing: 'border-box',
              height: 'fit-content'
            }}
          >
            {/* CHAPTER HEADER */}
            <div style={{
              borderBottom: `2px solid ${themeStyles.border}`,
              paddingBottom: '1.5rem',
              marginBottom: '2rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
                <span style={{
                  fontSize: '0.75rem',
                  fontWeight: '900',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  padding: '0.25rem 0.65rem',
                  borderRadius: '6px',
                  backgroundColor: 'rgba(236,201,75,0.15)',
                  color: themeStyles.accent,
                  border: `1px solid ${themeStyles.accent}`
                }}>
                  Section {currentChapterIndex + 1} of {chapters.length}
                </span>
                <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                  • {publication.category || 'Law Publication & Solved Papers'}
                </span>
                <span style={{
                  fontSize: '0.72rem',
                  fontWeight: '800',
                  padding: '0.2rem 0.5rem',
                  borderRadius: '4px',
                  backgroundColor: '#0284c7',
                  color: 'white',
                  textTransform: 'uppercase'
                }}>
                  🌐 {languageMode === 'auto' ? (publication.language || 'English') : languageMode}
                </span>
              </div>

              <h1 style={{
                fontSize: '1.85rem',
                fontWeight: '900',
                lineHeight: '1.3',
                margin: '0 0 0.5rem 0'
              }}>
                {currentChapter.title}
              </h1>

              {publication.authors && publication.authors.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.5rem', color: '#94a3b8', fontSize: '0.85rem', fontWeight: '700' }}>
                  <Award size={16} color={themeStyles.accent} />
                  <span>By {publication.authors.join(' & ')}</span>
                </div>
              )}
            </div>

            {/* READING CONTENT PARAGRAPHS WITH MULTILINGUAL & NASTALIQ / NASKH SUPPORT */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1.25rem',
              lineHeight: languageMode === 'urdu' || languageMode === 'arabic' ? '2.2' : '1.75',
              textAlign: languageMode === 'urdu' || languageMode === 'arabic' ? 'right' : 'justify',
              direction: languageMode === 'urdu' || languageMode === 'arabic' ? 'rtl' : 'ltr',
              fontFamily: languageMode === 'urdu' 
                ? '"Jameel Noori Nastaleeq", "Noto Nastaliq Urdu", "Urdu Typesetting", serif' 
                : languageMode === 'arabic' 
                  ? '"Traditional Arabic", "Scheherazade New", "Amiri", serif' 
                  : '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              fontSize: `${languageMode === 'urdu' || languageMode === 'arabic' ? fontSize + 3 : fontSize}px`
            }}>
              {paragraphs.map((para, pIdx) => {
                const trimmed = para.trim();

                // Decorative Divider
                if (trimmed === '--------------------' || trimmed === '---' || trimmed === '***') {
                  return (
                    <div key={pIdx} style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '0.75rem',
                      margin: '1.5rem 0',
                      color: themeStyles.accent,
                      opacity: 0.7
                    }}>
                      <div style={{ height: '1px', flex: 1, backgroundColor: themeStyles.border }} />
                      <span style={{ fontSize: '0.9rem', letterSpacing: '3px' }}>✦ ✦ ✦</span>
                      <div style={{ height: '1px', flex: 1, backgroundColor: themeStyles.border }} />
                    </div>
                  );
                }

                // Section / Hadith Header Banner (e.g. الْحَدِيثُ الْأَوَّلُ)
                const isSectionHeader = /^(الْحَدِيثُ|الحديث|الفصل|الباب|المقدمة|خَاتِمَةُ|خاتمة|پیش لفظ|باب اوّل|باب دوم|باب سوم|باب چہارم|Chapter \d+|Section \d+|CSS Examination|Question \d+)/i.test(trimmed);
                if (isSectionHeader) {
                  return (
                    <div 
                      key={pIdx}
                      style={{
                        marginTop: '2.25rem',
                        marginBottom: '1rem',
                        padding: '1rem 1.4rem',
                        borderRadius: '12px',
                        background: readerTheme === 'dark' 
                          ? 'linear-gradient(135deg, rgba(236,201,75,0.18) 0%, rgba(236,201,75,0.04) 100%)' 
                          : 'linear-gradient(135deg, #fef3c7 0%, #fffbeb 100%)',
                        borderRight: '5px solid #ecc94b',
                        borderLeft: `1px solid ${themeStyles.border}`,
                        borderTop: `1px solid ${themeStyles.border}`,
                        borderBottom: `1px solid ${themeStyles.border}`,
                        direction: 'rtl',
                        textAlign: 'right',
                        boxShadow: '0 4px 14px rgba(0,0,0,0.06)'
                      }}
                    >
                      <h3 style={{
                        margin: 0,
                        fontSize: `${fontSize + 3}px`,
                        fontWeight: '900',
                        fontFamily: '"Traditional Arabic", "Amiri", "Scheherazade New", "Noto Nastaliq Urdu", "Jameel Noori Nastaleeq", serif',
                        color: readerTheme === 'dark' ? '#ecc94b' : '#b45309',
                        lineHeight: '1.6'
                      }}>
                        {para}
                      </h3>
                    </div>
                  );
                }

                // Urdu Translation Card
                const isUrduTranslation = trimmed.startsWith('📖 اردو ترجمہ:') || 
                  trimmed.startsWith('📖 اردو ترجمہ') || 
                  trimmed.startsWith('اردو ترجمہ:') || 
                  trimmed.startsWith('📖 اردو اختتامیہ:');

                if (isUrduTranslation) {
                  return (
                    <div
                      key={pIdx}
                      style={{
                        margin: '1.25rem 0',
                        padding: '1.4rem 1.6rem',
                        borderRadius: '14px',
                        backgroundColor: readerTheme === 'dark' ? 'rgba(30, 41, 59, 0.75)' : '#f0fdf4',
                        border: `1.5px solid ${readerTheme === 'dark' ? '#334155' : '#bbf7d0'}`,
                        borderRight: '5px solid #10b981',
                        direction: 'rtl',
                        textAlign: 'right',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
                      }}
                    >
                      <div style={{
                        fontSize: '0.85rem',
                        fontWeight: '900',
                        color: '#10b981',
                        marginBottom: '0.75rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.4rem',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                      }}>
                        <span>📖 اردو ترجمہ و مفہوم (Urdu Translation)</span>
                      </div>
                      <div style={{
                        fontFamily: '"Noto Nastaliq Urdu", "Jameel Noori Nastaleeq", "Urdu Typesetting", serif',
                        fontSize: `${fontSize + 3}px`,
                        lineHeight: '2.3',
                        color: readerTheme === 'dark' ? '#f8fafc' : '#064e3b'
                      }}>
                        {trimmed.replace(/^📖\s*اردو\s*ترجمہ:?\s*/, '').replace(/^اردو\s*ترجمہ:?\s*/, '').replace(/^📖\s*اردو\s*اختتامیہ:?\s*/, '')}
                      </div>
                    </div>
                  );
                }

                // Fiqh / Commentary Notes Card
                const isFiqhNote = trimmed.startsWith('💡 فقہی و ایمانی فوائد:') || 
                  trimmed.startsWith('💡 فقہی نکتہ:') || 
                  trimmed.startsWith('💡');

                if (isFiqhNote) {
                  return (
                    <div
                      key={pIdx}
                      style={{
                        margin: '1rem 0',
                        padding: '1.1rem 1.4rem',
                        borderRadius: '12px',
                        backgroundColor: readerTheme === 'dark' ? 'rgba(56, 189, 248, 0.08)' : '#f0f9ff',
                        border: `1px solid ${readerTheme === 'dark' ? 'rgba(56, 189, 248, 0.25)' : '#bae6fd'}`,
                        borderRight: '5px solid #0284c7',
                        direction: 'rtl',
                        textAlign: 'right'
                      }}
                    >
                      <div style={{
                        fontFamily: '"Noto Nastaliq Urdu", "Jameel Noori Nastaleeq", "Urdu Typesetting", serif',
                        fontSize: `${fontSize + 2}px`,
                        lineHeight: '2.1',
                        color: readerTheme === 'dark' ? '#38bdf8' : '#0369a1'
                      }}>
                        {trimmed}
                      </div>
                    </div>
                  );
                }

                // Arabic Hadith Matn (with « » or Tashkeel or Sanad)
                const isArabicHadithMatn = /[\u0600-\u06FF]/.test(trimmed) && (
                  trimmed.includes('«') || 
                  trimmed.includes('»') || 
                  trimmed.includes('قَالَ رَسُولُ اللَّهِ') || 
                  trimmed.includes('عَنْ ') || 
                  trimmed.includes('رَوَاهُ') || 
                  trimmed.includes('مُتَّفَقٌ عَلَيْهِ') ||
                  trimmed.includes('بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ')
                );

                if (isArabicHadithMatn) {
                  return (
                    <div
                      key={pIdx}
                      style={{
                        margin: '1.25rem 0',
                        padding: '1.3rem 1.6rem',
                        borderRadius: '14px',
                        backgroundColor: readerTheme === 'dark' ? 'rgba(236, 201, 75, 0.07)' : '#fefce8',
                        border: `1.5px solid ${readerTheme === 'dark' ? 'rgba(236, 201, 75, 0.25)' : '#fef08a'}`,
                        borderRight: '5px solid #ecc94b',
                        direction: 'rtl',
                        textAlign: 'right',
                        fontFamily: '"Amiri", "Scheherazade New", "Traditional Arabic", serif',
                        fontSize: `${fontSize + 4}px`,
                        lineHeight: '2.3',
                        color: readerTheme === 'dark' ? '#fde047' : '#854d0e',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.06)'
                      }}
                    >
                      {trimmed}
                    </div>
                  );
                }

                // General Arabic Paragraph
                const isGeneralArabic = /[\u0600-\u06FF]/.test(trimmed);
                if (isGeneralArabic) {
                  return (
                    <p 
                      key={pIdx} 
                      style={{
                        margin: 0,
                        direction: 'rtl',
                        textAlign: 'right',
                        fontFamily: '"Noto Nastaliq Urdu", "Jameel Noori Nastaleeq", "Amiri", serif',
                        lineHeight: '2.2',
                        fontSize: `${fontSize + 2}px`
                      }}
                    >
                      {trimmed}
                    </p>
                  );
                }

                return (
                  <p 
                    key={pIdx} 
                    style={{
                      margin: 0,
                      textIndent: '1.5rem',
                      lineHeight: '1.75'
                    }}
                  >
                    {trimmed}
                  </p>
                );
              })}
            </div>

            {/* CHAPTER PAGINATION BOTTOM */}
            <div style={{
              marginTop: '3rem',
              paddingTop: '1.5rem',
              borderTop: `2px solid ${themeStyles.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '1rem'
            }}>
              <button
                disabled={currentChapterIndex === 0}
                onClick={() => {
                  setCurrentChapterIndex(prev => Math.max(0, prev - 1));
                  if (readerContainerRef.current) readerContainerRef.current.scrollTop = 0;
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  padding: '0.65rem 1.25rem',
                  borderRadius: '10px',
                  fontSize: '0.85rem',
                  fontWeight: '800',
                  backgroundColor: readerTheme === 'dark' ? '#1e293b' : '#e2e8f0',
                  color: themeStyles.text,
                  border: `1px solid ${themeStyles.border}`,
                  cursor: currentChapterIndex === 0 ? 'not-allowed' : 'pointer',
                  opacity: currentChapterIndex === 0 ? 0.35 : 1
                }}
              >
                <ChevronLeft size={16} />
                <span>Previous Section</span>
              </button>

              <span style={{
                fontSize: '0.85rem',
                fontFamily: 'monospace',
                color: '#94a3b8',
                fontWeight: '700'
              }}>
                Section {currentChapterIndex + 1} of {chapters.length}
              </span>

              <button
                disabled={currentChapterIndex === chapters.length - 1}
                onClick={() => {
                  setCurrentChapterIndex(prev => Math.min(chapters.length - 1, prev + 1));
                  if (readerContainerRef.current) readerContainerRef.current.scrollTop = 0;
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  padding: '0.65rem 1.25rem',
                  borderRadius: '10px',
                  fontSize: '0.85rem',
                  fontWeight: '900',
                  backgroundColor: themeStyles.accent,
                  color: '#0f172a',
                  border: 'none',
                  cursor: currentChapterIndex === chapters.length - 1 ? 'not-allowed' : 'pointer',
                  opacity: currentChapterIndex === chapters.length - 1 ? 0.35 : 1,
                  boxShadow: '0 2px 10px rgba(236,201,75,0.4)'
                }}
              >
                <span>Next Section</span>
                <ChevronRight size={16} />
              </button>
            </div>

          </div>
        </div>

      </div>

      {/* SCREEN CAPTURE & RECORDING SHIELD (ACTIVATES WHEN WINDOW BLURS) */}
      {screenBlurred && (
        <div 
          onClick={() => setScreenBlurred(false)}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 200000,
            backgroundColor: 'rgba(9, 13, 22, 0.97)',
            backdropFilter: 'blur(16px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: '2rem',
            cursor: 'pointer'
          }}
        >
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: 'rgba(239,68,68,0.2)',
            border: '2px solid #ef4444',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '1.5rem'
          }}>
            <EyeOff size={36} color="#ef4444" />
          </div>
          <h2 style={{
            fontSize: '1.65rem',
            fontWeight: '900',
            color: '#ffffff',
            margin: '0 0 0.75rem 0'
          }}>
            🔒 Content Shielded for Intellectual Property Security
          </h2>
          <p style={{
            fontSize: '0.9rem',
            color: '#cbd5e1',
            maxWidth: '520px',
            lineHeight: '1.6',
            margin: '0 0 1.5rem 0'
          }}>
            Screen capture, background recording, or external window switching was detected. Elum Law Digital Rights Management has shielded this publication to prevent unauthorized capture.
          </p>
          <button
            onClick={() => setScreenBlurred(false)}
            style={{
              padding: '0.75rem 2rem',
              borderRadius: '12px',
              backgroundColor: '#ecc94b',
              color: '#0f172a',
              fontWeight: '900',
              fontSize: '0.95rem',
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(236,201,75,0.4)'
            }}
          >
            Click to Resume Reading
          </button>
        </div>
      )}

      {/* FOOTER DRM STATUS */}
      <div style={{
        height: '32px',
        backgroundColor: '#070b14',
        borderTop: `1px solid ${themeStyles.border}`,
        padding: '0 1.25rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: '0.72rem',
        color: '#64748b',
        flexShrink: 0,
        fontFamily: 'monospace'
      }}>
        <span>ELUM LAW ASSOCIATES • DIGITAL DRM VAULT</span>
        <span>PROTECTED USER: {userEmail} • ACTIVE SESSION</span>
        <span>COPYRIGHT © 2026 ALL RIGHTS RESERVED</span>
      </div>

    </div>
  );
};

export default SecurePublicationReader;
